import { Record, List } from 'immutable';
import { sortBy } from 'lodash';

import { Answer } from './Answer';
import { Share } from './Share';

class Question extends Record(
  {
    answers: [],
    answerFeedback: [],
    appliedTermTranslationIds: [],
    assetTitle: null,
    body: null,
    childQuestions: [],
    command: null,
    contextQuestions: null,
    conversationId: null,
    category: null,
    createdAt: null,
    createdBy: null,
    deletedAt: null,
    deletedBy: null,
    followUps: [],
    id: null,
    isRestricted: false,
    isLatest: null,
    isRetry: false,
    organizationId: null,
    parentQuestionId: null,
    previousQuestionId: null,
    rephrasedQuestions: [],
    questionBreakdown: [],
    previousVersion: null,
    schema: null,
    schemas: null,
    shares: [],
    spaceIds: [],
    status: null,
    updatedAt: null,
    updatedBy: null,
    userId: null,
    versions: [],
    order: null,
    currentChartFollowUp: null,
    questionVersionSetId: null
  },
  'Question'
) {
  constructor(props) {
    super({
      ...props,
      answers: props?.answers?.map((answer) => new Answer(answer)),
      followUps: new List(
        sortBy(props?.followUps, (followUp) => followUp.createdAt)
          ?.filter((followUp) => !followUp.deletedAt)
          ?.map((followUp) => new Question(followUp))
      ),
      childQuestions: sortBy(props?.childQuestions, (question) => question.createdAt)?.map(
        (childQuestion) => new Question(childQuestion)
      ),
      versions: sortBy(props.versions || [], (version) => version.createdAt)
        ?.map((version) => new Question(version))
        .reverse(),
      answerFeedback: props?.answers?.[0]?.answerFeedback,
      shares: props?.shares?.map((share) => new Share(share))
    });
  }

  action(userId) {
    return this.shares?.find((share) => share.participant(userId))?.participant(userId)?.action;
  }

  share(userId) {
    return this.shares?.find((share) => share.participant(userId));
  }

  isHelpRequested(userId) {
    return this.answers.find((answer) =>
      answer?.answerFeedback.some((f) => f?.feedbackScore === -1 && f?.createdBy === userId)
    );
  }

  get answer() {
    return this.answers?.[0];
  }

  get exportFileName() {
    const questionText = this.body?.trim().split(/\s+/).slice(0, 5).join(' ');
    const questionId = this.id?.slice(-3);

    let exportFileName = questionText;
    if (questionId) {
      exportFileName += ` - ${questionId}`;
    }

    return exportFileName;
  }

  get isHelpReceived() {
    return this.answerFeedback?.some((f) => f.createdBy.includes('supper.co'));
  }

  get isChart() {
    return this.command === 'chart' || this.command === 'chart-refine';
  }

  get isClarify() {
    return this.answers?.[0]?.generatedBy === 'ClarifyQuestionProcessor';
  }

  get isCategoryQuestion() {
    return this.category !== null && this.category?.[0] !== 'A';
  }

  get lastFollowUp() {
    let current = this;
    let result = null;

    // Use `size` instead of `length` for Immutable.js Lists
    for (let i = current.followUps.size - 1; i >= 0; i -= 1) {
      // Use `.get(i)` instead of bracket notation
      const followUp = current.followUps.get(i);
      if (!followUp.isChart) {
        result = followUp;
        break;
      }
    }

    current = result;
    return current !== this ? current : null;
  }

  get lastAssetFollowUp() {
    const assetFollowUps = this.followUps.filter(
      (f) => !f.isChart && !f.isClarify && !f.isCategoryQuestion
    );

    if (assetFollowUps.size === 0) {
      return this;
    }

    // The top-most chart follow-up
    const current = assetFollowUps.get(0);

    // see if *that* follow-up has nested chart follow-ups
    if (current.followUps?.size > 0) {
      const { lastAssetFollowUp } = current;
      return lastAssetFollowUp;
    }
    return current;
  }

  get lastChartFollowUp() {
    const chartFollowUps = this.followUps.filter((f) => f.isChart);

    if (chartFollowUps.size === 0) {
      return null;
    }

    // The top-most chart follow-up
    const current = chartFollowUps.get(0);

    // see if *that* follow-up has nested chart follow-ups
    if (current.followUps?.size > 0) {
      const refinedChart = current.lastChartFollowUp;
      return refinedChart || current;
    }
    return current;
  }

  setCurrentChartFollowUp(question) {
    return this.set('currentChartFollowUp', question);
  }

  setLastChartFollowUp(newFollowUp) {
    // 1) Find first chart follow-up
    const chartIndex = this.followUps.findIndex((f) => f.isChart);

    if (chartIndex === -1) {
      return this.set('followUps', this.followUps.push(newFollowUp));
    }

    const firstChart = this.followUps.get(chartIndex);

    // If this is an update to an existing chart follow-up (same ID), replace it entirely
    if (firstChart.id === newFollowUp.id) {
      return this.setIn(['followUps', chartIndex], newFollowUp);
    }

    // If it has no further nested follow-ups, it's the last chart follow-up
    if (!firstChart.followUps || firstChart.followUps.size === 0) {
      // Using updateIn to replace the followUp at `chartIndex`
      return this.updateIn(['followUps', chartIndex], (f) =>
        f.set('followUps', new List([newFollowUp]))
      );
    }

    // Otherwise, it has nested follow-ups: recurse
    // We rely on the child's own `setLastChartFollowUp`
    // This merges the updated child back into this Record's followUps array.
    return this.updateIn(['followUps', chartIndex], (f) => f.setLastChartFollowUp(newFollowUp));
  }

  removeLastChartFollowUp() {
    const chartIndex = this.followUps.findIndex((f) => f.isChart);
    if (chartIndex === -1) return this; // No chart follow-ups to remove

    const chartFollowUp = this.followUps.get(chartIndex);
    // If this chart has no nested follow-ups, remove it entirely.
    if (!chartFollowUp.followUps || chartFollowUp.followUps.size === 0) {
      return this.set('followUps', this.followUps.delete(chartIndex));
    }

    // Otherwise, recurse into the nested follow-ups.
    const updatedChart = chartFollowUp.removeLastChartFollowUp();
    return this.updateIn(['followUps', chartIndex], () => updatedChart);
  }

  get statusMessage() {
    switch (this.status) {
      case null:
        return 'Analyzing your question...';
      case 'CategorizeQuestionProcessor':
      case 'QuestionCategoryProcessor':
      case 'ClarifyQuestionProcessor':
        return 'Interpreting your question...';
      case 'QuestionTextProcessor':
      case 'SchemaProcessor':
      case 'MultiSchemaProcessor':
      case 'MultiSchemaTableProcessor':
      case 'ColumnClusterProcessor':
      case 'EntityRecognitionProcessor':
      case 'LinkedSourceProcessor':
      case 'ParseDateProcessor':
        return 'Selecting data sources...';
      case 'ChartProcessor':
        return 'Plotting your chart...';
      case 'GetAnswerStepsProcessor':
        return 'Determining necessary steps...';
      case 'PromptProcessor':
      case 'PromptToSQLProcessor':
        return 'Constructing a database query';
      case 'HallucinatedTableProcessor':
      case 'QueryValidationProcessor':
      case 'QueryModificationProcessor':
      case 'QueryFilterProcessor':
      case 'CteSpoofProcessor':
      case 'MatViewSpoofProcessor':
      case 'QueryRunnerProcessor':
      case 'QueryFuzzyMatchProcessor':
        return 'Retrieving the data...';
      case 'AnswerValidationProcessor':
      case 'ChartValidationProcessor':
      case 'EmptyAnswerProcessor':
        return 'Confirming results...';
      case 'FormatAnswerTableProcessor':
        return 'Refining the response format...';
      case 'TextResponseProcessor':
        return 'Searching for insights';
      case 'QueryExplanationProcessor':
        return 'Creating your summary...';
      case 'StepProcessor':
        return 'Preparing necessary calculations...';
      case 'RetryProcessor':
        return 'Retrying...';
      case 'QuestionBreakdownProcessor':
      case 'QuestionRephrasingProcessor':
        return 'Providing suggestions...';
      case 'GlobalPlannerProcessor':
        return 'Planning the answer...';
      case 'UseMetricsProcessor':
        return 'Looking for relevant KPIs...';
      case 'ScriptRunnerProcessor':
        return 'Running Deep Analysis...';
      case 'TermTranslationsProcessor':
        return 'Exploring business context...';
      case 'SemanticTermTranslationsProcessor':
        return 'Applying business logic...';
      case 'QuestionReassemblyProcessor':
        return 'Combining insights...';
      default:
        return this.status;
    }
  }

  get statusProgress() {
    switch (this.status) {
      case 'CategorizeQuestionProcessor':
      case 'QuestionCategoryProcessor':
      case 'QuestionTextProcessor':
        return 0;
      case 'SchemaProcessor':
      case 'LinkedSourceProcessor':
      case 'ChartProcessor':
      case 'MultiSchemaProcessor':
      case 'MultiSchemaTableProcessor':
      case 'ParseDateProcessor':
        return 15;
      case 'PromptProcessor':
      case 'PromptToSQLProcessor':
        return 35;
      case 'HallucinatedTableProcessor':
      case 'QueryValidationProcessor':
      case 'QueryModificationProcessor':
      case 'QueryFilterProcessor':
      case 'CteSpoofProcessor':
      case 'MatViewSpoofProcessor':
      case 'QueryRunnerProcessor':
        return 45;
      case 'AnswerValidationProcessor':
      case 'ChartValidationProcessor':
      case 'SelectWinningBeamProcessor':
        return 55;
      case 'EmptyAnswerProcessor':
        return 65;
      case 'FormatAnswerTableProcessor':
        return 75;
      case 'TextResponseProcessor':
        return 85;
      case 'QueryExplanationProcessor':
        return 90;
      case 'QuestionBreakdownProcessor':
      case 'QuestionRephrasingProcessor':
        return 100;
      case 'RetryProcessor':
      default:
        return 0;
    }
  }

  get hasFailedNewVersions() {
    return this.versions.some(
      (version) => version.answer?.error && version.createdAt > this.createdAt
    );
  }

  get isRefreshing() {
    return (
      this.status === 'REFRESHING' ||
      this.versions.some((version) => version.createdAt > this.createdAt && !version.answer?.error)
    );
  }

  get successfulVersions() {
    return (
      this.versions
        .filter((version) => version.answer && !version.answer?.isCanned && !version.answer?.error)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || []
    );
  }

  get previousVersion() {
    return Array.isArray(this.successfulVersions) && this.successfulVersions.length > 0
      ? this.successfulVersions[0]
      : null;
  }
}

export { Question };
